"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, Download, Save } from "lucide-react";
import { useState, useEffect } from "react";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";

interface PARData {
  par: string;
  closing_balance: number;
  opening_balance: number;
  variance: number;
  variance_percentage: number;
  exceeds_materiality: boolean;
  qualitatively_material?: "Yes" | "No" | "";
  considered_significant: "Significant" | "Not Significant" | "";
  remarks?: string;
  note_line?: string;
  type?: string;
}

interface MaterialityThresholds {
  group_materiality?: number;
  performance_materiality?: number;
  clearly_trivial_threshold?: number;
  revised_materiality?: number;
}

interface FilteredAnalyticalTableProps {
  title: string;
  data: PARData[];
  materiality: MaterialityThresholds;
  onChange?: (data: PARData[]) => void;
  readonly?: boolean;
  filterTypes: string[];
  excludeTypes?: string[];
  icon?: React.ReactNode;
  useYesNoForSignificance?: boolean;
  closingBalanceDate?: string;
  openingBalanceDate?: string;
  showAbcotd?: boolean;
  showSaveButton?: boolean;
  areResponsesLoaded?: boolean;
}

export function FilteredAnalyticalTable({
  title,
  data,
  materiality,
  onChange,
  readonly = false,
  filterTypes,
  excludeTypes = [],
  icon,
  useYesNoForSignificance = false,
  closingBalanceDate = "",
  openingBalanceDate = "",
  showAbcotd = false,
  showSaveButton = false,
  areResponsesLoaded = true,
}: FilteredAnalyticalTableProps) {
  const { isArchivalMode } = useArchivalMode();
  const [searchTerm, setSearchTerm] = useState("");
  const [applicableMateriality, setApplicableMateriality] = useState<number | undefined>(undefined);
  const [companyName, setCompanyName] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [localData, setLocalData] = useState<PARData[]>(data);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.error("Failed to load company name:", error);
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load materiality from Planning_UserResponses.json
  useEffect(() => {
    const loadMateriality = async () => {
      try {
        if (!window.planningAPI?.loadResponses) return;

        const companyName = await (window as any).planningAPI?.getCompanyName?.("combined") || "ABC Private Limited";
        
        const sharePointResponses = await window.planningAPI.loadResponses({
          companyName,
          sectionKey: "materiality",
        });

        if (!sharePointResponses?.success || !sharePointResponses?.data) {
          // Fallback to materiality prop
          setApplicableMateriality(
            materiality.revised_materiality && materiality.revised_materiality > 0
              ? materiality.revised_materiality
              : materiality.group_materiality
          );
          return;
        }

        const responses = sharePointResponses.data.responses || sharePointResponses.data;

        // Check for revised materiality first
        const revisedMateriality = responses["revisedMateriality"];
        if (revisedMateriality?.determinedMateriality && revisedMateriality.determinedMateriality > 0) {
          setApplicableMateriality(parseFloat(revisedMateriality.determinedMateriality.toString()) || 0);
          return;
        }

        // Fallback to M-3 textResponse
        const m3Response = responses["M-3"];
        if (m3Response?.textResponse) {
          const textValue = typeof m3Response.textResponse === "string"
            ? m3Response.textResponse
            : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
          const materialityValue = parseFloat(textValue) || 0;
          setApplicableMateriality(materialityValue > 0 ? materialityValue : undefined);
          return;
        }

        // Final fallback to materiality prop
        setApplicableMateriality(
          materiality.revised_materiality && materiality.revised_materiality > 0
            ? materiality.revised_materiality
            : materiality.group_materiality
        );
      } catch (error) {
        console.error("Failed to load materiality from Planning_UserResponses.json:", error);
        // Fallback to materiality prop
        setApplicableMateriality(
          materiality.revised_materiality && materiality.revised_materiality > 0
            ? materiality.revised_materiality
            : materiality.group_materiality
        );
      }
    };

    loadMateriality();
  }, [materiality]);

  // Update localData when data prop changes
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  // Filter data by types and search term
  const filteredData = localData
    .filter((item) => {
      // If excludeTypes is specified, exclude those types
      if (excludeTypes.length > 0) {
        if (excludeTypes.includes(item.type || "")) {
          return false;
        }
      }
      // If filterTypes is specified, include only those types
      if (filterTypes.length > 0) {
        return filterTypes.includes(item.type || "");
      }
      return true;
    })
    .map((item) => {
      // Recalculate variance percentage (variance / opening * 100)
      const variance = item.closing_balance - item.opening_balance;
      const variance_percentage =
        item.opening_balance !== 0
          ? (variance / item.opening_balance) * 100
          : item.opening_balance === 0 && item.closing_balance !== 0
            ? 100
            : 0;

      // Always recalculate exceeds materiality: if absolute closing balance > materiality, then true
      const exceeds_materiality = applicableMateriality
        ? Math.abs(item.closing_balance) > Math.abs(applicableMateriality)
        : false;

      return {
        ...item,
        variance,
        variance_percentage,
        exceeds_materiality,
      };
    })
    .filter((item) => {
      const matchesSearch =
        !searchTerm ||
        item.par.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.note_line &&
          item.note_line.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchesSearch;
    });

  const handleSignificanceChange = (
    index: number,
    newValue: "Significant" | "Not Significant" | "Yes" | "No",
  ) => {
    if (readonly || !onChange) return;

    const updatedData = [...localData];
    const actualIndex = localData.findIndex(
      (item) => item.par === filteredData[index].par,
    );
    if (actualIndex !== -1) {
      // Convert Yes/No to Significant/Not Significant for storage
      const storageValue =
        useYesNoForSignificance && newValue === "Yes"
          ? "Significant"
          : useYesNoForSignificance && newValue === "No"
            ? "Not Significant"
            : newValue;

      updatedData[actualIndex] = {
        ...updatedData[actualIndex],
        considered_significant: storageValue as "Significant" | "Not Significant" | "",
      };
      setLocalData(updatedData);
      onChange(updatedData);
    }
  };

  const handleQualitativelyMaterialChange = (
    index: number,
    newValue: "Yes" | "No",
  ) => {
    if (readonly || !onChange) return;

    const updatedData = [...localData];
    const actualIndex = localData.findIndex(
      (item) => item.par === filteredData[index].par,
    );
    if (actualIndex !== -1) {
      updatedData[actualIndex] = {
        ...updatedData[actualIndex],
        qualitatively_material: newValue,
      };
      setLocalData(updatedData);
      onChange(updatedData);
    }
  };

  const handleRemarksChange = (
    index: number,
    newValue: string,
  ) => {
    console.log("handleRemarksChange called", { index, newValue, readonly, hasOnChange: !!onChange });
    if (readonly || !onChange) {
      console.log("Early return:", { readonly, hasOnChange: !!onChange });
      return;
    }

    const updatedData = [...localData];
    const actualIndex = localData.findIndex(
      (item) => item.par === filteredData[index].par,
    );
    console.log("Finding item:", { actualIndex, par: filteredData[index]?.par });
    if (actualIndex !== -1) {
      updatedData[actualIndex] = {
        ...updatedData[actualIndex],
        remarks: newValue,
      };
      setLocalData(updatedData);
      onChange(updatedData);
      console.log("Updated data:", updatedData[actualIndex]);
    } else {
      console.warn("Could not find item to update");
    }
  };

  const handleSave = async () => {
    if (!onChange) return;

    setIsSaving(true);
    try {
      // Get company name if not already loaded
      const currentCompanyName = companyName || await getCompanyName("combined") || "ABC Private Limited";
      
      // Use the localData which contains the latest user changes
      // Filter to only include items that match the current table's filter types
      const itemsToSave = localData.filter((item) => {
        if (filterTypes.length > 0) {
          return filterTypes.includes(item.type || "");
        }
        if (excludeTypes.length > 0) {
          return !excludeTypes.includes(item.type || "");
        }
        return true;
      });

      // Determine which field name to use based on filter types
      const isBalanceSheet = filterTypes.includes("Asset") || filterTypes.includes("Liability") || filterTypes.includes("Equity");
      const isProfitLoss = filterTypes.includes("Income") || filterTypes.includes("Expense");
      
      const fieldName = isBalanceSheet 
        ? "PreliminaryAnalyticalProcedures_BalanceSheetItems"
        : isProfitLoss
        ? "PreliminaryAnalyticalProcedures_ProfitLossItems"
        : "PreliminaryAnalyticalProcedures_BalanceSheetItems"; // Default to Balance Sheet

      // Prepare the data to save in the format expected by Planning_UserResponses.json
      const saveData: Record<string, any> = {
        [fieldName]: itemsToSave.map((row) => {
          // Always recalculate exceeds_materiality based on closing balance and materiality
          // This ensures we save the current calculated value, not the old saved value
          const recalculatedExceedsMateriality = applicableMateriality
            ? Math.abs(row.closing_balance) > Math.abs(applicableMateriality)
            : false;

          // Recalculate variance and variance_percentage to ensure they're up to date
          const recalculatedVariance = row.closing_balance - row.opening_balance;
          const recalculatedVariancePercentage =
            row.opening_balance !== 0
              ? (recalculatedVariance / row.opening_balance) * 100
              : row.opening_balance === 0 && row.closing_balance !== 0
                ? 100
                : 0;

          return {
            par: row.par,
            abcotd: row.par, // Using par as ABCOTD
            closing_balance: row.closing_balance,
            opening_balance: row.opening_balance,
            variance: recalculatedVariance,
            variance_percentage: recalculatedVariancePercentage,
            exceeds_materiality: recalculatedExceedsMateriality, // Save the recalculated boolean value
            qualitatively_material: row.qualitatively_material || "",
            considered_significant: row.considered_significant || "",
            remarks: row.remarks || "",
            note_line: row.note_line || "",
            type: row.type || "",
          };
        }),
      };

      console.log(`Saving ${isBalanceSheet ? "Balance Sheet" : "Profit & Loss"} Items:`, {
        companyName: currentCompanyName,
        sectionKey: "preliminary-analytical",
        itemCount: itemsToSave.length,
        fieldName,
        saveData
      });

      // Save using planning API
      if ((window as any).planningAPI?.saveAllResponses) {
        const result = await (window as any).planningAPI.saveAllResponses({
          companyName: currentCompanyName,
          sectionKey: "preliminary-analytical",
          responses: saveData,
        });

        console.log("Save result:", result);

        if (result && result.success) {
          console.log(`${isBalanceSheet ? "Balance Sheet" : "Profit & Loss"} Items saved successfully`);
          alert(`${isBalanceSheet ? "Balance Sheet" : "Profit & Loss"} Items saved successfully!`);
        } else {
          console.error("Failed to save:", result?.error || "Unknown error");
          alert(`Failed to save: ${result?.error || "Unknown error"}`);
        }
      } else {
        console.error("planningAPI.saveAllResponses not available");
        alert("Save functionality not available. Please refresh the page.");
      }
    } catch (error) {
      console.error(`Error saving ${title}:`, error);
      alert(`Error saving: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(2)}%`;
  };

  const exportToCSV = () => {
    const headers = [
      showAbcotd ? "ABCOTD" : "PAR",
      closingBalanceDate ? `Closing Balance (${closingBalanceDate})` : "Closing Balance",
      openingBalanceDate ? `Opening Balance (${openingBalanceDate})` : "Opening Balance",
      "Variance",
      "Variance (%)",
      "Exceeds Materiality?",
      "Considered Significant?",
      "Type",
    ];

    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) =>
        [
          `"${row.par}"`,
          row.closing_balance,
          row.opening_balance,
          row.variance,
          row.variance_percentage.toFixed(2),
          row.exceeds_materiality ? "Yes" : "No",
          `"${row.considered_significant}"`,
          `"${row.type || ""}"`,
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.toLowerCase().replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="border-white/10 bg-black/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-white">
            {icon}
            {title}
          </CardTitle>
          <div className="flex gap-2">
            <Button
              onClick={exportToCSV}
              size="sm"
              variant="outline"
              className="border-white/10 text-white/80 hover:bg-white/10"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>

        {applicableMateriality && (
          <div className="flex flex-wrap gap-4 text-sm text-white/70">
            {materiality.revised_materiality &&
            materiality.revised_materiality > 0 ? (
              <span>
                Revised Materiality:{" "}
                {formatCurrency(materiality.revised_materiality)}
              </span>
            ) : (
              <span>
                Planning Materiality:{" "}
                {formatCurrency(materiality.group_materiality || 0)}
              </span>
            )}
            {materiality.performance_materiality && (
              <span>
                Performance Materiality:{" "}
                {formatCurrency(materiality.performance_materiality)}
              </span>
            )}
            {materiality.clearly_trivial_threshold && (
              <span>
                Clearly Trivial:{" "}
                {formatCurrency(materiality.clearly_trivial_threshold)}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-white/50" />
            <Input
              placeholder={`Search ${showAbcotd ? "ABCOTD" : title.toLowerCase()} items...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-white/10 pl-10 text-white"
            />
          </div>
          <Badge variant="outline" className="border-white/10 text-white/70">
            {filteredData.length} items
          </Badge>
          {showSaveButton && (
            <Button
              onClick={handleSave}
              disabled={isSaving || isArchivalMode}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSaving ? (
                <>
                  <Save className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        <div className="rounded-md border border-white/10">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-white/5">
                <TableHead className="text-white/80">
                  {showAbcotd ? "ABCOTD" : "PAR"}
                </TableHead>
                <TableHead className="text-right text-white/80">
                  {closingBalanceDate ? `Closing Balance (${closingBalanceDate})` : "Closing Balance"}
                </TableHead>
                <TableHead className="text-right text-white/80">
                  {openingBalanceDate ? `Opening Balance (${openingBalanceDate})` : "Opening Balance"}
                </TableHead>
                <TableHead className="text-right text-white/80">
                  Variance
                </TableHead>
                <TableHead className="text-right text-white/80">
                  Variance (%)
                </TableHead>
                <TableHead className="text-center text-white/80">
                  Exceeds Materiality?
                </TableHead>
                <TableHead className="text-center text-white/80">
                  Qualitatively Material?
                </TableHead>
                <TableHead className="text-center text-white/80">
                  Considered Significant?
                </TableHead>
                <TableHead className="text-center text-white/80 min-w-[320px] w-96">
                  Remarks
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="py-8 text-center text-white/60"
                  >
                    No {title.toLowerCase()} data available.
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((row, index) => (
                  <TableRow
                    key={row.par}
                    className="border-white/10 hover:bg-white/5"
                  >
                    <TableCell className="font-medium text-white">
                      <div>
                        <div>{row.par}</div>
                        {row.note_line && (
                          <div className="mt-1 text-xs text-white/50">
                            {row.note_line}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-white">
                      {formatCurrency(row.closing_balance)}
                    </TableCell>
                    <TableCell className="text-right text-white">
                      {formatCurrency(row.opening_balance)}
                    </TableCell>
                    <TableCell
                      className={`text-right font-medium ${
                        (() => {
                          // For Profit & Loss items, use adverse/favourable logic
                          if (title === "Profit & Loss Items") {
                            const itemType = row.type || "";
                            if (itemType === "Expense") {
                              // Expenses: increasing (variance > 0) is Adverse (red), decreasing (variance < 0) is Favourable (green)
                              return row.variance > 0 ? "text-red-400" : row.variance < 0 ? "text-green-400" : "text-white";
                            } else if (itemType === "Income") {
                              // Incomes: decreasing (variance < 0, more negative) is Favourable (green), increasing (variance > 0) is Adverse (red)
                              return row.variance < 0 ? "text-green-400" : row.variance > 0 ? "text-red-400" : "text-white";
                            }
                          }
                          // For Balance Sheet items, use standard logic (green for positive, red for negative)
                          return row.variance > 0
                            ? "text-green-400"
                            : row.variance < 0
                              ? "text-red-400"
                              : "text-white";
                        })()
                      }`}
                    >
                      {formatCurrency(row.variance)}
                    </TableCell>
                    <TableCell
                      className={`text-right font-medium ${
                        Math.abs(row.variance_percentage) > 10
                          ? "text-yellow-400"
                          : "text-white"
                      }`}
                    >
                      {formatPercentage(row.variance_percentage)}
                    </TableCell>
                    <TableCell className="text-center">
                      {areResponsesLoaded ? (
                        <Badge
                          variant={
                            row.exceeds_materiality ? "destructive" : "secondary"
                          }
                          className={
                            row.exceeds_materiality
                              ? "border-red-500/30 bg-red-500/20 text-red-300"
                              : "border-green-500/30 bg-green-500/20 text-green-300"
                          }
                        >
                          {row.exceeds_materiality ? "Yes" : "No"}
                        </Badge>
                      ) : (
                        <span className="text-white/30">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {areResponsesLoaded ? (
                        <Select
                          value={row.qualitatively_material || undefined}
                          onValueChange={(newValue: string) => {
                            if (newValue) {
                              handleQualitativelyMaterialChange(
                                index,
                                newValue as "Yes" | "No",
                              );
                            }
                          }}
                          disabled={readonly || isArchivalMode}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent className="z-[100] border-white/10 bg-black/95 text-white">
                            <SelectItem value="Yes" className="cursor-pointer hover:bg-white/10 focus:bg-white/10">
                              Yes
                            </SelectItem>
                            <SelectItem value="No" className="cursor-pointer hover:bg-white/10 focus:bg-white/10">
                              No
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-white/30">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {areResponsesLoaded ? (
                        <Select
                          value={
                            useYesNoForSignificance
                              ? row.considered_significant === "Significant"
                                ? "Yes"
                                : row.considered_significant === "Not Significant"
                                  ? "No"
                                  : undefined
                              : row.considered_significant || undefined
                          }
                          onValueChange={(newValue: string) => {
                            if (newValue) {
                              handleSignificanceChange(
                                index,
                                newValue as "Significant" | "Not Significant" | "Yes" | "No",
                              );
                            }
                          }}
                          disabled={readonly || isArchivalMode}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent className="z-[100] border-white/10 bg-black/95 text-white">
                            {useYesNoForSignificance ? (
                              <>
                                <SelectItem value="Yes" className="cursor-pointer hover:bg-white/10 focus:bg-white/10">
                                  Yes
                                </SelectItem>
                                <SelectItem value="No" className="cursor-pointer hover:bg-white/10 focus:bg-white/10">
                                  No
                                </SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="Significant" className="cursor-pointer hover:bg-white/10 focus:bg-white/10">
                                  Significant
                                </SelectItem>
                                <SelectItem value="Not Significant" className="cursor-pointer hover:bg-white/10 focus:bg-white/10">
                                  Not Significant
                                </SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-white/30">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center min-w-[320px] w-96">
                      {areResponsesLoaded ? (
                        <Textarea
                          value={row.remarks || ""}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleRemarksChange(index, e.target.value);
                          }}
                          onKeyDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          disabled={readonly || isArchivalMode}
                          placeholder="Enter remarks..."
                          className="min-h-[60px] w-full min-w-[300px] border-white/10 bg-white/5 text-white placeholder:text-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      ) : (
                        <span className="text-white/30">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {filteredData.length > 0 && (
          <div className="mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <div className="mb-1 text-white/70">Total Items</div>
              <div className="font-semibold text-white">
                {filteredData.length}
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <div className="mb-1 text-white/70">Exceeding Materiality</div>
              <div className="font-semibold text-red-300">
                {filteredData.filter((item) => item.exceeds_materiality).length}
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-black/20 p-3">
              <div className="mb-1 text-white/70">Marked Significant</div>
              <div className="font-semibold text-yellow-300">
                {
                  filteredData.filter(
                    (item) => item.considered_significant === "Significant",
                  ).length
                }
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
